/**
 * Which columns show: the custom column picker and its localStorage persistence, column
 * groups, the generated visibility stylesheet, and the page tooltip list built from headers.
 *
 * Calls on sibling mixins via `this`: CoreState (researchDepth, isMobile, isLargeScreen), Filtering
 * (selectedFantasyPosition), UserLeague (selectedUserLeagueId, hasUserLeagueSelected).
 */
import Alpine from '/js/alpine/core@3.14.9/alpine.esm.js';

export default () => ({
    customColumnsLocalStorageKey: 'ds-rankings-custom-columns',
    columnGroups: {},
    customColumnsOptions: {
        rankings: [],
    },
    pageTooltipsOpen: false,
    customColumnsOpen: false,
    allColumns: [],
    selectedColumns: {},
    defaultMobileColumnAttributes: [],
    defaultDesktopColumnAttributes: [],

    initSelectedColumns() {
        // Initialize localStorage for custom columns
        const stored = localStorage.getItem(this.customColumnsLocalStorageKey);
        this.selectedColumns = stored ? JSON.parse(stored) : this.getDefaultColumns();
    },

    initColumnVisibilityStyles() {
        // Column visibility is one generated stylesheet keyed on data-attribute instead of
        // per-cell x-show bindings; the effect re-runs whenever any reactive state read
        // inside isVisibleColumn changes (depth, filters, breakpoints, custom columns).
        Alpine.effect(() => this.updateColumnVisibilityStyles());
        this.stripInlineColumnDisplay(document.querySelector('#rankingsTable'));
    },

    get availableColumns() {
        return this.allColumns
            .filter(col => {
                // if column has a depth, compare it to the current research depth
                // if column does not have a depth, always include it. This is because
                // most rankings pages will not use research depth so the default 'rankings'
                // depth will always be included.
                if (col.depth) {
                    return col.depth === this.researchDepth;
                }
                return true;
            })
            .map(col => col.attribute);
    },

    isVisibleColumnGroup(group) {
        const selected = this.selectedColumns?.[this.researchDepth];
        if (selected && group in selected) {
            return selected[group];
        }
        switch (this.selectedFantasyPosition) {
            case 'QB':
                return ['passing', 'rushing'].includes(group);
            case 'RB':
            case 'WR':
            case 'TE':
            case 'FLEX':
                return ['rushing', 'receiving'].includes(group);
            case 'K':
                return group === 'kicking';
            case 'DEF':
                return group === 'defensive';
            case 'IDP':
            case 'DL':
            case 'LB':
            case 'DB':
                return ['idp'].includes(group);
            default:
                if (this.defaultMobileColumnAttributes.length > 0 && this.isMobile) {
                    return this.defaultMobileColumnAttributes.includes(group);
                }
                return (
                    ['passing', 'rushing', 'receiving', 'kicking', 'defensive'].includes(group) ||
                    (this.selectedUserLeagueId && 'returning' === group)
                );
        }
    },

    isVisibleColumn(attribute, el) {
        if (el && el.dataset.colgroup) {
            const group = el.dataset.colgroup;
            let groupIsVisible = true;
            if (this.selectedColumns && this.selectedColumns[this.researchDepth] && group in this.selectedColumns[this.researchDepth]) {
                groupIsVisible = this.selectedColumns[this.researchDepth][group];
            }

            if (!groupIsVisible) {
                return false;
            }
            groupIsVisible = this.isVisibleColumnGroup(group);
            if (!groupIsVisible) {
                return false;
            }
            if (attribute.endsWith('Total')) {
                return true;
            }
            if (this.columnGroups[group]) {
                return this.columnGroups[group].expanded;
            }
        }
        // On mobile, ensure IDP columns are visible when IDP position is selected
        // This bypasses defaultMobileColumnAttributes which doesn't include IDP columns
        const isIdpPosition = ['DL', 'LB', 'DB', 'IDP'].includes(this.selectedFantasyPosition?.toUpperCase());
        if (this.isMobile && isIdpPosition && attribute.startsWith('idp')) {
            return this.isVisibleColumnGroup('idp');
        }

        if (this.isMobile && attribute.startsWith('def')) {
            return this.isVisibleColumnGroup('defensive');
        }

        if (this.isMobile && attribute.startsWith('k')) {
            return this.isVisibleColumnGroup('kicking');
        }

        // A user league has no stored previous week. Hide Trend so the public-board
        // delta is not read as the change in the league's 3D Value.
        if (attribute === 'dsValueDelta' && this.hasUserLeagueSelected()) {
            return false;
        }

        if (this.selectedColumns && this.selectedColumns[this.researchDepth] && attribute in this.selectedColumns[this.researchDepth]) {
            // Once the user has selected a column to be shown or hidden, respect that choice
            return this.selectedColumns[this.researchDepth][attribute];
        } else if (this.isMobile && this.defaultMobileColumnAttributes.length > 0) {
            // If on mobile, use default mobile columns if they exist for initial visibility
            const negated = this.defaultMobileColumnAttributes[0] === '-';
            const includes = this.defaultMobileColumnAttributes.includes(attribute);
            if (negated) {
                return !includes;
            }
            return includes;
        } else if (this.isLargeScreen && this.defaultDesktopColumnAttributes.length > 0) {
            const negated = this.defaultDesktopColumnAttributes[0] === '-';
            const includes = this.defaultDesktopColumnAttributes.includes(attribute);
            if (negated) {
                return !includes;
            }
            return includes;
        }
        // Projections offers Trend in the column picker. It stays off until the user checks it.
        if (this.researchDepth === 'projections' && attribute === 'dsValueDelta') {
            return false;
        }
        return true;
    },

    // Applies column visibility as one generated stylesheet scoped to the rankings container.
    // Hiding via [data-attribute] rules replaces the per-cell x-show bindings whose inline
    // style writes forced full-table style invalidation on every toggle (pathological in
    // WebKit, where the style-attribute writes also re-ran :has() selector invalidation).
    updateColumnVisibilityStyles() {
        const table = document.querySelector('#rankingsTable');
        const container = document.querySelector('#rankingsTableContainer');
        if (!table || !container) {
            return;
        }

        const hiddenSelectors = new Set();
        const seenAttributes = new Set();
        // Scan row cells as well as thead ths: some columns render cells with no header th
        // (analysis text columns, and depth-change swaps replace only the rows), and each
        // attribute's first cell supplies the data-colgroup context isVisibleColumn needs
        const cells = table.querySelectorAll('thead th[data-attribute], tbody[data-player-row] td[data-attribute]');
        for (const cell of cells) {
            const attribute = cell.dataset.attribute;
            if (seenAttributes.has(attribute)) {
                continue;
            }
            seenAttributes.add(attribute);
            if (!this.isVisibleColumn(attribute, cell)) {
                hiddenSelectors.add(`#rankingsTableContainer [data-attribute="${attribute}"]`);
            }
        }

        // Group labels live on the expand row with data-colgroup only (no data-attribute),
        // so the scan above misses them. HTMX table swaps also drop their x-show bindings.
        for (const th of table.querySelectorAll('.positional-projections-expand-row th[data-colgroup]')) {
            const group = th.dataset.colgroup;
            if (!this.isVisibleColumnGroup(group)) {
                hiddenSelectors.add(`#rankingsTableContainer .positional-projections-expand-row th[data-colgroup="${group}"]`);
            }
        }

        // Analysis-style text columns top-align the player rows. Detect them on the row
        // cells, not thead - depth swaps replace only the rows and these columns render
        // without a header th. A cell without data-attribute (td.analysis) has no
        // visibility toggle, so its presence alone counts as visible.
        const analysisCell = table.querySelector(
            'tbody[data-player-row] td.analysis, tbody[data-player-row] td.dynasty-analysis, tbody[data-player-row] td.bottom-line-column'
        );
        const analysisColumnVisible =
            !!analysisCell && (!analysisCell.dataset.attribute || this.isVisibleColumn(analysisCell.dataset.attribute, analysisCell));

        let styleEl = document.getElementById('rankings-column-visibility');
        if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = 'rankings-column-visibility';
            document.head.appendChild(styleEl);
        }
        // Projections sizes columns from the group row, so hidden subcolumns have to
        // leave the table. Other rankings tables are table-layout:fixed, which still
        // reserves a display:none column, so those cells stay in the row at width 0.
        const hiddenCss = [...hiddenSelectors].join(',\n');
        const fixedLayoutCollapse = `#rankingsTable:not(:has(.positional-projections-expand-row)) :is(${hiddenCss})`;
        const fixedCollapseCss = [
            'display: table-cell',
            'width: 0',
            'min-width: 0',
            'max-width: 0',
            'padding: 0',
            'border-width: 0',
            'overflow: hidden',
            'font-size: 0',
            'line-height: 0',
            'visibility: hidden',
        ].join('; ');
        const collapsedText = `${fixedLayoutCollapse} * { font-size: 0; line-height: 0; margin: 0; padding: 0; }`;
        const css = hiddenSelectors.size
            ? `${hiddenCss} { display: none; }\n${fixedLayoutCollapse} { ${fixedCollapseCss}; }\n${collapsedText}`
            : '';
        if (styleEl.textContent !== css) {
            styleEl.textContent = css;
        }

        // Drives the is-analysis-column-visible styles in _rankings-table.scss (replaces a
        // :has() selector over per-cell inline styles)
        container.classList.toggle('is-analysis-column-visible', analysisColumnVisible);
    },

    // SSR renders depth-hidden columns with inline display:none so the first paint matches
    // the default state. The generated stylesheet owns visibility after init, so the inline
    // values are cleared here (at init, on load-rows fragments, and after HTMX table swaps).
    stripInlineColumnDisplay(root) {
        if (!root) {
            return;
        }
        root.querySelectorAll('th[data-attribute][style], td[data-attribute][style]').forEach(cell => {
            if (cell.style.display) {
                cell.style.display = '';
            }
        });
    },

    get visibleColumns() {
        return this.selectedColumns[this.researchDepth];
    },

    get allColumnsVisible() {
        return this.columnOptions.every(col => this.isVisibleColumn(col.value));
    },

    getDefaultColumns() {
        if (this.isMobile && this.defaultMobileColumnAttributes.length > 0) {
            return Object.fromEntries(this.availableColumns.map(col => [col, this.defaultMobileColumnAttributes.includes(col)]));
        }
        return Object.fromEntries(this.availableColumns.map(col => [col, true]));
    },

    toggleColumn(columnOption, checked) {
        const attribute = columnOption.value;
        const selected = { ...(this.visibleColumns ?? this.getDefaultColumns()), [attribute]: checked };
        this.selectedColumns = { ...this.selectedColumns, [this.researchDepth]: selected };
        this.persistCustomColumns();
    },

    get columnOptions() {
        if (this.researchDepth === 'projections' && !this.selectedUserLeagueId) {
            // remove 'returning' from the options if no user league is selected
            return (this.customColumnsOptions[this.researchDepth] || []).filter(col => col.value !== 'returning');
        }
        return this.customColumnsOptions[this.researchDepth] || [];
    },

    toggleAllColumns(event) {
        const checked = event.target.checked;
        const newState = Object.fromEntries(this.customColumnsOptions[this.researchDepth].map(col => [col.value, checked]));
        this.selectedColumns = { ...this.selectedColumns, [this.researchDepth]: newState };
        this.persistCustomColumns();
    },

    persistCustomColumns() {
        localStorage.setItem(this.customColumnsLocalStorageKey, JSON.stringify(this.selectedColumns));
    },

    toggleColumnGroupExpansion(group) {
        if (!this.columnGroups[group]) {
            return;
        }
        const expand = !this.columnGroups[group].expanded;
        for (const key of Object.keys(this.columnGroups)) {
            this.columnGroups[key].expanded = expand && key === group;
        }
        this.updateColumnVisibilityStyles();
    },
    isExpandedColumnGroup(group) {
        return this.columnGroups[group] && this.columnGroups[group].expanded;
    },

    getColumnGroupColspan(group) {
        if (this.columnGroups[group]) {
            return this.columnGroups[group].expanded ? this.columnGroups[group].count : 1;
        }
        return 1;
    },

    get pageTooltips() {
        const tooltips = [];
        const ths = this.$refs.rankingsTable.querySelectorAll('th[data-attribute]') || [];
        for (let th of ths) {
            if (th && th.hasAttribute('data-ds-tooltip')) {
                const attribute = th.dataset.attribute;
                if (!attribute || !this.isVisibleColumn(attribute, th)) {
                    continue; // Skip if attribute is not visible
                }
                const label = th.querySelector('span')?.textContent || '';
                // If is a "Total" column, skip it
                if (label.endsWith('Total')) {
                    continue;
                }
                tooltips.push({
                    value: th.getAttribute('data-ds-tooltip'),
                    label: label,
                });
            }
        }
        const half = Math.ceil(tooltips.length / 2);
        return {
            left: tooltips.slice(0, half),
            right: tooltips.slice(half),
        };
    },
});
